import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import ScrollToTopButton from "../components/ScrollToTopButton";
import HeroPosterCard from "../components/HeroPosterCard";
import { CATEGORY_ORDER, categoryToSlug, slugToCategory } from "../data/publications";
import { getPublication } from "../api/publicationsApi";
import { API_ORIGIN } from "../api/authApi";
import { getPublicationIcon } from "../data/publicationIcons";

// Section A: public. One page per publication (/publications/:slug).
// The first category's cards are shown by default; clicking a category
// button switches to just that category's cards, animated in as a
// staggered grid. Clicking the active category again hides them.
//
// The selected category is mirrored into the "?category=" query string
// (see handleCategoryClick) so every category has its own
// shareable/bookmarkable URL — restored on load below.
function PublicationDetailPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const categoryFromUrl = slugToCategory(searchParams.get("category"));
  const [selectedCategory, setSelectedCategory] = useState(
    CATEGORY_ORDER.includes(categoryFromUrl) ? categoryFromUrl : CATEGORY_ORDER[0]
  );

  useEffect(() => {
    getPublication(slug)
      .then((pub) => {
        const topicsByCategory = {};
        for (const t of pub.topics) {
          (topicsByCategory[t.category] ??= []).push(t);
        }
        setPublication({ ...pub, topics: topicsByCategory });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>در حال بارگذاری...</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (notFound || !publication) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>این نشریه یافت نشد.</p>
          <Link to="/publications" className="back-link">
            ← بازگشت به نشریه‌ها
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const revealedCards = selectedCategory ? publication.topics[selectedCategory] || [] : [];

  function handleCategoryClick(category) {
    // Clicking the already-active category hides its cards again.
    const next = selectedCategory === category ? null : category;
    setSelectedCategory(next);

    const params = new URLSearchParams(searchParams);
    if (next) {
      params.set("category", categoryToSlug(next));
    } else {
      params.delete("category");
    }
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="site">
      <SiteHeader />

      <main>
        <section className="hero pub-hero pub-detail-hero">
          <div className="hero-content">
            <Link to="/publications" className="back-link pub-detail-back">
              ← بازگشت به نشریه‌ها
            </Link>
            <div className="pub-detail-icon">{getPublicationIcon(publication.icon_key)}</div>
            <span className="hero-eyebrow">{publication.subtitle}</span>
            <h1>{publication.title}</h1>
            <p>{publication.description}</p>

            <ul className="pub-tags pub-detail-tags">
              {publication.tags.map((tag) => (
                <li key={tag} className="pub-tag">
                  {tag}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <div className="dimension-divider">
          <span className="dimension-line" />
          <span className="dimension-label">شش دسته</span>
          <span className="dimension-line" />
        </div>

        <section className="pub-topics-section">
          <div className="pub-category-buttons">
            {CATEGORY_ORDER.map((category) => (
              <button
                key={category}
                type="button"
                className={
                  "pub-category-button" +
                  (selectedCategory === category ? " pub-category-button--active" : "")
                }
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {selectedCategory ? (
            <>
              <p className="pub-topics-hint">
                موضوعات «{selectedCategory}» در شمارهٔ {publication.title}
              </p>
              <ul className="pub-topic-grid">
                {revealedCards.map((topic, index) => (
                  <li
                    className="pub-topic-card--emerge"
                    style={{ animationDelay: `${index * 70}ms` }}
                    key={topic.id}
                  >
                    <Link to={`/publications/${slug}/topics/${topic.id}`} className="pub-topic-card-link">
                      <HeroPosterCard
                        titleTag="h3"
                        kicker={topic.category}
                        title={topic.title_fa || topic.title_en}
                        subtitle={topic.description_fa || topic.description_en}
                        imageSrc={topic.image_path ? `${API_ORIGIN}${topic.image_path}` : undefined}
                        imageAlt={topic.title_fa || topic.title_en}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="pub-topics-hint">
              برای دیدن موضوعات این شماره، یکی از دسته‌های بالا را انتخاب کنید.
            </p>
          )}
        </section>
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}

export default PublicationDetailPage;
